import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@via/hooks/useAuth.tsx";
import { ROUTES } from "@via/utils/constants.tsx";
import {
  consumePostLoginRedirect,
  isBackendRoute,
  normalizeRedirectPath,
} from "@via/utils/post-login-redirect";
import type { MfaSessionResponse } from "common";

/**
 * Where to go once the second factor issued a session. The SPA's own targets win: the
 * ?redirect the login or invite page carried (an invite page accepts its invitation on
 * arrival), then the redirect an SSO button remembered before leaving. Then the server's
 * validated next (an OAuth or SAML resume the SPA never saw), then the activity page.
 */
export const mfaDestination = (params: {
  redirect: string | null;
  next: string;
  email: string;
}): string => {
  // "/" says nothing, and a /login/ page would bounce a signed in user back here.
  const redirect =
    params.redirect === "/" || params.redirect?.startsWith("/login/")
      ? null
      : params.redirect;
  const candidate = redirect ?? consumePostLoginRedirect() ?? params.next;
  if (candidate === ROUTES.SELF_SIGNUP) {
    return `${ROUTES.SELF_SIGNUP}?email=${encodeURIComponent(params.email)}`;
  }
  const normalized = normalizeRedirectPath(candidate);
  return !normalized || normalized === "/" ? ROUTES.ACTIVITY : normalized;
};

/**
 * The unsaved recovery codes reminder, which continues to `destination` afterwards. Every
 * sign-in path that finds `mfaCodesPending` (or unacknowledged codes) goes through here.
 */
export const codesReminderPath = (destination: string): string =>
  destination.split(/[?#]/)[0] === ROUTES.PROFILE_SECURITY
    ? `${ROUTES.PROFILE_SECURITY}?codes=pending`
    : `${ROUTES.PROFILE_SECURITY}?codes=pending&continue=${encodeURIComponent(destination)}`;

/** A backend target such as the OAuth consent resume needs a full page load, like useLogin does. */
export const goTo = (
  destination: string,
  navigate: (to: string) => void
): void => {
  if (isBackendRoute(destination)) window.location.assign(destination);
  else navigate(destination);
};

/** Stores the signed in user and returns where to go next. Call it before navigating. */
export const useFinishSignIn = (redirect: string | null) => {
  const { setUser } = useAuth();
  const queryClient = useQueryClient();
  return (response: MfaSessionResponse): string => {
    setUser(response.user);
    void queryClient.invalidateQueries({ queryKey: ["/api/auth/v1/status"] });
    void queryClient.invalidateQueries({ queryKey: ["/api/auth/v1/user"] });
    return mfaDestination({
      redirect,
      next: response.next,
      email: response.user.email,
    });
  };
};
